import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { LogOut, X } from "lucide-react";

export function HandoverDialog({ onClose, onSaved }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queue");
        // Filter to still-open assigned to me — backend counts happen server-side; here just show current queue
        setPreview((data || []).filter((q) => q.status !== "Completed"));
      } catch (err) { console.debug("handover preview fetch failed", err); }
    })();
  }, []);

  const submit = async () => {
    if (!text.trim()) { toast.error("Handover note is required"); return; }
    try {
      setSaving(true);
      const { data } = await api.post("/handovers", { text: text.trim() });
      toast.success(`Handover posted · attached to ${data.patients.length} patient${data.patients.length === 1 ? "" : "s"}`);
      onSaved?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save handover"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-slate-200" data-testid="handover-dialog">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-semibold flex items-center gap-2"><LogOut className="w-4 h-4 text-[#1976D2]" /> Shift handover</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs text-slate-500">Will be auto-attached to every still-open patient currently assigned to you. The next-shift doctor sees this note when they open the chart.</div>
          <textarea
            data-testid="handover-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            placeholder="e.g. Bed 5 (Nakato) — awaiting CBC, plan repeat vitals at 22:00; Bed 12 (Mukisa) — analgesia pending review after imaging"
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
          />
          <div className="text-[11px] text-slate-500">Currently open in queue: {preview.length} patient{preview.length === 1 ? "" : "s"} · handover attaches only to those assigned to you.</div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50 rounded-b-2xl">
          <button onClick={onClose} className="h-9 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
          <button data-testid="handover-submit" onClick={submit} disabled={saving} className="h-9 px-4 rounded-lg bg-[#1976D2] text-white text-sm font-medium hover:bg-[#1565C0] disabled:opacity-60">{saving ? "Posting…" : "Sign & post handover"}</button>
        </div>
      </div>
    </div>
  );
}

/** Small inline banner shown above a patient chart / admission detail. Fetches latest handover for the patient. */
export function HandoverBanner({ patientId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/handovers?patient_id=${encodeURIComponent(patientId)}&limit=3`);
        setItems(data || []);
      } catch (err) { console.debug("handover banner fetch failed", err); } finally { setLoading(false); }
    })();
  }, [patientId]);
  if (loading || items.length === 0) return null;
  const latest = items[0];
  return (
    <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm" data-testid="handover-banner">
      <div className="flex items-center gap-2 text-[#B45309] font-semibold text-xs uppercase tracking-wide"><LogOut className="w-3.5 h-3.5" /> Prior-shift handover</div>
      <div className="mt-1 text-slate-800 whitespace-pre-line">{latest.text}</div>
      <div className="text-[11px] text-slate-500 mt-1">— {latest.doctor_name} · {latest.shift_name} shift · {new Date(latest.created_at).toLocaleString()}</div>
    </div>
  );
}
